import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import type { ArcLimits } from '@/api/generated';
import { SortableItem, SortableList } from '@/components/ui/SortableList';

import { limitReasons } from './arcSchemas';
import { inOrder, KIND_COLOURS, type ArcItem, type ArcSection } from './arcTypes';
import { ReasonedButton } from './ReasonedButton';
import { SectionCard } from './SectionCard';
import { KindDot } from './sectionLook';

/** What a section and its rows can ask the page to do. */
export interface SectionHandlers {
  onSetVisible: (section: ArcSection, visible: boolean) => void;
  onDelete: (section: ArcSection) => void;
  onRename: (section: ArcSection) => void;
  onRemoveItem: (section: ArcSection, item: ArcItem) => void;
  onMoveItem: (section: ArcSection, item: ArcItem) => void;
  onReorderItems: (section: ArcSection, ids: string[]) => void;
  /** Opens the Add Content picker for a section. */
  onAddContent: (section: ArcSection) => void;
}

interface SectionsListProps extends SectionHandlers {
  sections: ArcSection[];
  /** The limits in force, from the reply. */
  limits: ArcLimits;
  readOnly: boolean;
  onNew: () => void;
  onReorder: (ids: string[]) => void;
}

/**
 * The ARC's sections, in the order app users see them. Each can be dragged
 * by its handle, moved with the keyboard on the handle, or moved one place
 * with Move up and Move down; every move is announced.
 */
export function SectionsList({
  sections,
  limits,
  readOnly,
  onNew,
  onReorder,
  ...handlers
}: SectionsListProps) {
  const ordered = inOrder(sections);
  const names = new Map(ordered.map((section) => [section.id, section.name]));
  const reasons = limitReasons({ sections, limits });

  return (
    <Box component="section" aria-labelledby="arc-builder-sections-heading">
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Box sx={{ minWidth: 0, flex: '1 1 14rem' }}>
          <Typography
            id="arc-builder-sections-heading"
            variant="h6"
            component="h2"
            sx={{ fontWeight: 600 }}
          >
            ARC Sections
          </Typography>
          <Typography variant="body2">
            Drag sections to reorder. Drag content within each section.
          </Typography>
        </Box>
        {/* The wireframe's legend: what the dot beside each section's name means. */}
        <Box
          data-testid="arc-builder-sections-legend-group"
          sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}
        >
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <KindDot mixed={false} />
            <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
              Standard
              <br />
              Section
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <KindDot mixed />
            <Typography variant="caption" sx={{ lineHeight: 1.2 }}>
              Custom
              <br />
              Mixed-Content Section
            </Typography>
          </Box>
        </Box>
        <Box
          // Purple, as the legend's mixed-content dot: what it adds is a custom section.
          sx={{
            '& .MuiButton-root:not(.Mui-disabled)': {
              bgcolor: KIND_COLOURS.mixed,
              color: 'text.primary',
              borderColor: 'text.primary',
              '&:hover': { bgcolor: '#9575CD' },
            },
          }}
        >
          <ReasonedButton
            reason={readOnly ? null : reasons.newSection}
            onClick={onNew}
            disabled={readOnly}
            testId="arc-builder-new-section-button"
          >
            + New Custom Section
          </ReasonedButton>
        </Box>
      </Box>
      <SortableList
        ids={ordered.map((section) => section.id)}
        nameOf={(id) => names.get(id) ?? 'Section'}
        onReorder={onReorder}
        disabled={readOnly}
      >
        <Box
          data-testid="arc-builder-sections-list"
          // As the page's grid: the cards may be narrower than their titles (B28).
          sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'minmax(0, 1fr)' }}
        >
          {ordered.map((section) => (
            <SortableItem
              key={section.id}
              id={section.id}
              testId={`arc-builder-section-${section.id}`}
              // Move up and Move down are in the section's Actions (the wireframe).
              moveButtons={false}
            >
              {({ ref, style }) => (
                <Box ref={ref} style={style}>
                  <SectionCard
                    section={section}
                    readOnly={readOnly}
                    addReason={reasons.addTo(section.id)}
                    showReason={readOnly ? null : reasons.show(section.id)}
                    {...handlers}
                  />
                </Box>
              )}
            </SortableItem>
          ))}
        </Box>
      </SortableList>
    </Box>
  );
}
